import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  KeyRound,
  Loader2,
  Mail,
  ShieldAlert,
  Trash2,
} from 'lucide-react'

import {
  EMPLOYMENT_FRACTIONS,
  calculateMonthlyNorm,
  formatEmploymentFraction,
} from '@czas/shared'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'
import FullScreenSpinner from '@/components/layout/FullScreenSpinner'
import TempPasswordModal from '@/features/admin/temp-password-modal'
import InvitationModal from '@/features/admin/invitation-modal'
import {
  useAdminUser,
  useAnonymizeUser,
  useResendInvitation,
  useResetUserPassword,
  useUpdateUser,
} from '@/features/admin/use-admin-users'
import { useJobPositions } from '@/features/admin/use-job-positions'
import {
  useAdminVacationBalances,
  useUpdateVacationBalance,
} from '@/features/admin/use-admin-vacation'
import { useSession } from '@/lib/auth'
import { useCanManageAdmins } from '@/lib/acl'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

const schema = z.object({
  name: z
    .string()
    .min(1, 'Wpisz imię i nazwisko')
    .max(120, 'Maksymalnie 120 znaków'),
  role: z.enum(['EMPLOYEE', 'ADMIN']),
  active: z.boolean(),
  positionId: z.string(),
  hasChildrenUnder14: z.boolean(),
  hourlyRate: z.number().min(0).max(999999.99).nullable(),
})

type FormValues = z.infer<typeof schema>

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return format(new Date(iso), "d MMM yyyy 'o' HH:mm", { locale: pl })
}

export default function UserEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const session = useSession()

  const canManageAdmins = useCanManageAdmins()
  const userQuery = useAdminUser(id)
  const update = useUpdateUser()
  const resetPassword = useResetUserPassword()
  const resendInvitation = useResendInvitation()
  const anonymize = useAnonymizeUser()
  const positionsQuery = useJobPositions()

  const [confirmReset, setConfirmReset] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [shownPassword, setShownPassword] = useState<{
    tempPassword: string
    name: string
  } | null>(null)
  const [shownInvitation, setShownInvitation] = useState<{
    name: string
    email: string
    invitationLink: string
    expiresAt: string
  } | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      role: 'EMPLOYEE',
      active: true,
      positionId: '',
      hasChildrenUnder14: false,
      hourlyRate: null,
    },
  })

  const [employmentFraction, setEmploymentFraction] = useState<number | null>(
    null,
  )
  const [fractionDirty, setFractionDirty] = useState(false)

  // Preview normy bieżącego miesiąca dla wybranego ułamka (KP-zgodne).
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const currentNorm = calculateMonthlyNorm(currentYear, currentMonth)
  const currentTarget =
    employmentFraction !== null
      ? Math.ceil(currentNorm * employmentFraction)
      : null
  const monthLabel = now.toLocaleString('pl-PL', {
    month: 'long',
    year: 'numeric',
  })

  useEffect(() => {
    if (userQuery.data) {
      const u = userQuery.data.user
      form.reset({
        name: u.name,
        role: u.role,
        active: u.active,
        positionId: u.positionId ?? '',
        hasChildrenUnder14: u.hasChildrenUnder14 ?? false,
        hourlyRate: u.hourlyRate ?? null,
      })
      setEmploymentFraction(u.employmentFraction ?? null)
      setFractionDirty(false)
    }
    // form.reset jest stabilne, omijamy go w deps żeby uniknąć remount na każdy render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.data])

  function handleFractionChange(value: string) {
    setEmploymentFraction(value === 'null' ? null : parseFloat(value))
    setFractionDirty(true)
  }

  if (!id) {
    return <Navigate to="/admin/users" replace />
  }

  if (userQuery.isLoading) {
    return <FullScreenSpinner />
  }

  if (userQuery.error || !userQuery.data) {
    return (
      <div className="max-w-md space-y-4">
        <Link
          to="/admin/users"
          className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Wróć do listy
        </Link>
        <Alert variant="error">
          {userQuery.error
            ? apiErrorMessage(userQuery.error)
            : 'Nie znaleziono użytkownika.'}
        </Alert>
      </div>
    )
  }

  const user = userQuery.data.user
  const isSelf = session.data?.id === user.id
  const isDeleted = user.deletedAt != null
  const canDelete = canManageAdmins && !isDeleted && !isSelf

  if (isDeleted) {
    return (
      <div className="max-w-md space-y-4">
        <Link
          to="/admin/users"
          className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Wróć do listy
        </Link>
        <Alert variant="info">
          <div className="font-medium">Pracownik usunięty</div>
          <div className="text-xs mt-1">
            Anonimizacja:{' '}
            {format(new Date(user.deletedAt!), "d MMM yyyy 'o' HH:mm", {
              locale: pl,
            })}
            . Dane osobowe (imię, email, hasło) zostały usunięte. Historia
            pracy (sesje, urlopy, audit log) zachowana pod anonimowym ID dla
            zgodności prawnej.
          </div>
        </Alert>
      </div>
    )
  }

  if (user.role === 'ADMIN' && !canManageAdmins) {
    return (
      <div className="max-w-md space-y-4">
        <Link
          to="/admin/users"
          className="text-sm text-text-muted hover:text-text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Wróć do listy
        </Link>
        <Alert variant="info">
          To konto należy do administratora. Tylko inny administrator może je
          edytować.
        </Alert>
      </div>
    )
  }

  async function onSubmit(values: FormValues) {
    if (!id) return
    try {
      await update.mutateAsync({
        id,
        name: values.name,
        positionId: values.positionId || null,
        hasChildrenUnder14: values.hasChildrenUnder14,
        hourlyRate: values.hourlyRate,
        // Self-protection: nie wysyłamy active ani role własnego konta
        ...(isSelf
          ? {}
          : { role: values.role, active: values.active }),
        ...(fractionDirty ? { employmentFraction } : {}),
      })
      toast.success('Zmiany zapisane')
      setFractionDirty(false)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function handleResetConfirm() {
    if (!id) return
    setConfirmReset(false)
    try {
      const result = await resetPassword.mutateAsync(id)
      setShownPassword({
        tempPassword: result.temporaryPassword,
        name: result.user.name,
      })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  async function handleResendInvitation() {
    if (!id) return
    try {
      const result = await resendInvitation.mutateAsync(id)
      setShownInvitation({
        name: result.user.name,
        email: result.user.email,
        invitationLink: result.invitationLink,
        expiresAt: result.expiresAt,
      })
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <>
      <div className="max-w-2xl space-y-6">
        <div>
          <Link
            to="/admin/users"
            className="text-sm text-text-muted hover:text-text-primary mb-4 inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Wróć do listy
          </Link>
          <h1 className="text-2xl font-semibold">{user.name}</h1>
          <p className="text-sm text-text-muted">{user.email}</p>
          {!user.hasPassword && (
            <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-warning bg-warning/10 px-2.5 py-1 rounded-pill">
              <Clock className="h-3 w-3" aria-hidden />
              Czeka na akceptację zaproszenia
            </span>
          )}
        </div>

        {isSelf && (
          <Alert variant="info">
            <span className="inline-flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" aria-hidden />
              To Twoje konto. Możesz zmienić tylko imię — opcje roli i statusu
              są zablokowane.
            </span>
          </Alert>
        )}

        <section className="rounded-card border border-border-subtle bg-bg-elevated p-6">
          <h2 className="text-lg font-semibold mb-4">Dane konta</h2>

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="space-y-4"
          >
            <div>
              <Label htmlFor="name">Imię i nazwisko</Label>
              <Input
                id="name"
                invalid={!!form.formState.errors.name}
                {...form.register('name')}
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div>
              <Label>Email</Label>
              <Input value={user.email} disabled readOnly />
              <p className="mt-1 text-xs text-text-faint">
                Email nie jest edytowalny — utrudnia audyt zmian.
              </p>
            </div>

            <div>
              <Label>Rola</Label>
              <div className="flex gap-4 mt-1">
                <label
                  className={cn(
                    'flex items-center gap-2 text-sm',
                    isSelf ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="radio"
                    value="EMPLOYEE"
                    disabled={isSelf}
                    {...form.register('role')}
                  />
                  Pracownik
                </label>
                <label
                  className={cn(
                    'flex items-center gap-2 text-sm',
                    isSelf ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="radio"
                    value="ADMIN"
                    disabled={isSelf}
                    {...form.register('role')}
                  />
                  Admin
                </label>
              </div>
            </div>

            <div>
              <Label htmlFor="positionId">Stanowisko (opcjonalne)</Label>
              <select
                id="positionId"
                disabled={positionsQuery.isLoading}
                className="mt-1 w-full rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent disabled:opacity-50"
                {...form.register('positionId')}
              >
                <option value="">— Brak —</option>
                {(positionsQuery.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employment-fraction">Wymiar etatu</Label>
              <p className="text-xs text-text-faint">
                Ułamek etatu pracownika. Miesięczny target jest wyliczany
                dynamicznie wg art. 130 Kodeksu pracy — różni się co miesiąc w
                zależności od liczby dni roboczych i świąt. Pozostaw „Brak"
                jeśli pracownik nie ma określonego wymiaru (indykator się nie
                pokaże).
              </p>
              <select
                id="employment-fraction"
                value={
                  employmentFraction === null
                    ? 'null'
                    : employmentFraction.toFixed(3)
                }
                onChange={(e) => handleFractionChange(e.target.value)}
                className="rounded-card border border-border-subtle bg-bg-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent min-w-[220px]"
              >
                <option value="null">Brak (bez indykatora)</option>
                {EMPLOYMENT_FRACTIONS.slice()
                  .reverse()
                  .map((f) => (
                    <option key={f.value} value={f.value.toFixed(3)}>
                      {f.label}
                    </option>
                  ))}
              </select>
              {employmentFraction !== null && currentTarget !== null && (
                <p className="text-xs text-text-muted">
                  Norma {monthLabel}:{' '}
                  <strong className="text-text-primary">{currentNorm}h</strong>
                  {' × '}
                  {formatEmploymentFraction(employmentFraction)} ={' '}
                  <strong className="text-text-primary">
                    {currentTarget}h
                  </strong>
                </p>
              )}
            </div>

            <div>
              <Label>Status konta</Label>
              <label
                className={cn(
                  'mt-1 flex items-center gap-2 text-sm',
                  isSelf ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
              >
                <input
                  type="checkbox"
                  disabled={isSelf}
                  {...form.register('active')}
                />
                Aktywny (może się logować)
              </label>
              <p className="mt-1 text-xs text-text-faint">
                Dezaktywacja wyloguje pracownika i zablokuje logowanie. Historia
                sesji pozostaje.
              </p>
            </div>

            <div>
              <Label htmlFor="hourlyRate">
                Stawka godzinowa (PLN/h netto)
              </Label>
              <div className="mt-1 relative">
                <Input
                  id="hourlyRate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999999.99"
                  placeholder="np. 75.00"
                  className="pr-12"
                  {...form.register('hourlyRate', {
                    setValueAs: (v) =>
                      v === '' || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-faint pointer-events-none">
                  zł/h
                </span>
              </div>
              <p className="mt-1 text-xs text-text-muted">
                Używana do szacunku miesięcznej pensji w raporcie. Pozostaw
                puste jeśli nie ustawiona.
              </p>
            </div>

            <div>
              <Label>Uprawnienia urlopowe</Label>
              <label className="mt-1 flex items-start gap-3 rounded-card border border-border-subtle bg-bg-surface p-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...form.register('hasChildrenUnder14')}
                  className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-accent focus:ring-brand-accent"
                />
                <span className="flex-1">
                  <span className="text-sm font-medium text-text-primary">
                    Pracownik ma dziecko do 14 r.ż.
                  </span>
                  <span className="mt-0.5 block text-xs text-text-muted">
                    Uprawnienie do urlopu „opieka nad dzieckiem" (art. 188 KP —
                    2 dni/rok, 100% płatne). Pracownik bez tej flagi nie widzi
                    tej opcji w aplikacji.
                  </span>
                </span>
              </label>
            </div>

            {update.error && (
              <Alert variant="error">{apiErrorMessage(update.error)}</Alert>
            )}

            <Button
              type="submit"
              loading={update.isPending}
              disabled={!form.formState.isDirty && !fractionDirty}
            >
              Zapisz zmiany
            </Button>
          </form>
        </section>

        <VacationBalanceSection userId={user.id} />

        <section className="rounded-card border border-border-subtle bg-bg-elevated p-6">
          <h2 className="text-lg font-semibold mb-1">Bezpieczeństwo</h2>
          {!user.hasPassword ? (
            <>
              <p className="text-sm text-text-muted mb-4">
                Pracownik nie ustawił jeszcze hasła. Wyślij ponownie zaproszenie
                zamiast resetu — wygeneruje to nowy link i unieważni poprzedni.
              </p>
              <Button
                variant="secondary"
                onClick={handleResendInvitation}
                disabled={resendInvitation.isPending}
              >
                {resendInvitation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Mail className="h-4 w-4" aria-hidden />
                )}
                Wyślij ponownie zaproszenie
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-text-muted mb-4">
                Reset wyloguje pracownika ze wszystkich urządzeń i wymusi zmianę
                hasła przy kolejnym logowaniu.
              </p>

              <Button
                variant="secondary"
                onClick={() => setConfirmReset(true)}
                disabled={resetPassword.isPending}
              >
                {resetPassword.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <KeyRound className="h-4 w-4" aria-hidden />
                )}
                Zresetuj hasło
              </Button>
            </>
          )}
        </section>

        <section className="rounded-card border border-border-subtle bg-bg-elevated p-6">
          <h2 className="text-lg font-semibold mb-4">Informacje</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-text-faint text-xs uppercase tracking-wider">
                Utworzono
              </dt>
              <dd className="mt-0.5">{formatDate(user.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-text-faint text-xs uppercase tracking-wider">
                Ostatnia zmiana hasła
              </dt>
              <dd className="mt-0.5">
                {user.passwordChangedAt
                  ? formatDate(user.passwordChangedAt)
                  : 'Nigdy (hasło tymczasowe)'}
              </dd>
            </div>
            <div>
              <dt className="text-text-faint text-xs uppercase tracking-wider">
                RODO zaakceptowane
              </dt>
              <dd className="mt-0.5">
                {user.rodoSeenAt ? formatDate(user.rodoSeenAt) : 'Jeszcze nie'}
              </dd>
            </div>
            <div>
              <dt className="text-text-faint text-xs uppercase tracking-wider">
                ID
              </dt>
              <dd className="mt-0.5 font-mono text-xs">{user.id}</dd>
            </div>
          </dl>
        </section>

        {canDelete && (
          <section className="rounded-card border border-danger/40 bg-danger/5 p-6">
            <h2 className="text-lg font-semibold text-danger flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5" aria-hidden />
              Strefa zagrożenia
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Usunięcie pracownika to operacja nieodwracalna. Dane osobowe
              zostaną zanonimizowane (imię → „Pracownik usunięty", email →
              wyzerowany, hasło → wymazane). Historia pracy (sesje, urlopy,
              audit log) zostanie zachowana pod anonimowym ID dla zgodności
              prawnej.
            </p>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={anonymize.isPending}
              className="inline-flex items-center gap-2 rounded-action border border-danger/60 bg-danger/10 px-3 py-2 text-sm font-medium text-danger hover:bg-danger/20 disabled:opacity-50 transition"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              Usuń pracownika
            </button>
          </section>
        )}
      </div>

      {confirmReset && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
        >
          <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle
                className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
                aria-hidden
              />
              <div>
                <h2 className="text-lg font-semibold mb-1">
                  Zresetować hasło?
                </h2>
                <p className="text-sm text-text-muted">
                  Wygenerujemy nowe hasło tymczasowe dla <strong>{user.name}</strong>.
                  Pracownik zostanie wylogowany ze wszystkich urządzeń.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="secondary"
                onClick={() => setConfirmReset(false)}
                type="button"
              >
                Anuluj
              </Button>
              <Button onClick={handleResetConfirm} type="button">
                Tak, resetuj
              </Button>
            </div>
          </div>
        </div>
      )}

      {shownPassword && (
        <TempPasswordModal
          name={shownPassword.name}
          title="Nowe hasło tymczasowe"
          description={
            <>
              Nowe hasło dla <strong>{shownPassword.name}</strong>. Skopiuj
              i przekaż osobiście — nie wyświetlimy go ponownie. Stare hasło
              i sesje zostały unieważnione.
            </>
          }
          tempPassword={shownPassword.tempPassword}
          onClose={() => setShownPassword(null)}
        />
      )}

      {shownInvitation && (
        <InvitationModal
          name={shownInvitation.name}
          email={shownInvitation.email}
          invitationLink={shownInvitation.invitationLink}
          expiresAt={shownInvitation.expiresAt}
          title="Nowe zaproszenie wysłane"
          onClose={() => setShownInvitation(null)}
        />
      )}

      {confirmDelete && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
        >
          <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
            <h2
              id="confirm-delete-title"
              className="text-xl font-semibold mb-2 flex items-center gap-2"
            >
              <AlertTriangle className="h-5 w-5 text-danger" aria-hidden />
              Usuń pracownika?
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Operacja usunie konto <strong>{user.name}</strong> ({user.email}).
              Tej operacji nie można cofnąć. Historia pracy (sesje, urlopy,
              audit log) zostanie zachowana jako anonimowa.
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setConfirmDelete(false)}
                className="flex-1"
                type="button"
                disabled={anonymize.isPending}
              >
                Anuluj
              </Button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await anonymize.mutateAsync(user.id)
                    toast.success('Pracownik usunięty')
                    navigate('/admin/users', { replace: true })
                  } catch (err) {
                    toast.error(apiErrorMessage(err))
                    setConfirmDelete(false)
                  }
                }}
                disabled={anonymize.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-action bg-danger px-4 py-2.5 text-sm font-medium text-bg-elevated hover:opacity-90 disabled:opacity-50 transition"
              >
                {anonymize.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                )}
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function yearOptions(): number[] {
  const current = new Date().getFullYear()
  const out: number[] = []
  for (let y = current - 1; y <= current + 3; y++) out.push(y)
  return out
}

const balanceSchema = z.object({
  totalDays: z.coerce.number().int().min(0).max(100),
  usedDays: z.coerce.number().int().min(0),
})
type BalanceValues = z.infer<typeof balanceSchema>

function VacationBalanceSection({ userId }: { userId: string }) {
  const [year, setYear] = useState(() => new Date().getFullYear())
  const balancesQuery = useAdminVacationBalances(year)
  const update = useUpdateVacationBalance()

  const entry = balancesQuery.data?.balances.find((b) => b.user.id === userId)

  const form = useForm<BalanceValues>({
    resolver: zodResolver(balanceSchema),
    defaultValues: { totalDays: 26, usedDays: 0 },
  })

  useEffect(() => {
    if (entry) {
      form.reset({
        totalDays: entry.balance.totalDays,
        usedDays: entry.balance.usedDays,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.balance.totalDays, entry?.balance.usedDays, year])

  async function onSubmit(values: BalanceValues) {
    if (values.usedDays > values.totalDays) {
      toast.error('Wykorzystane dni nie mogą przekraczać puli.')
      return
    }
    try {
      await update.mutateAsync({
        userId,
        year,
        totalDays: values.totalDays,
        usedDays: values.usedDays,
      })
      toast.success('Bilans zapisany')
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <section className="rounded-card border border-border-subtle bg-bg-elevated p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-lg font-semibold">Bilans urlopu</h2>
        <div className="flex items-center gap-2">
          <label htmlFor="balance-year" className="text-sm text-text-muted">
            Rok:
          </label>
          <select
            id="balance-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 rounded-action border border-border-strong bg-bg-elevated px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {balancesQuery.isLoading && (
        <p className="text-sm text-text-muted inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Ładuję bilans…
        </p>
      )}

      {!balancesQuery.isLoading && (
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label htmlFor="balance-total">Dni przysługujące</Label>
              <Input
                id="balance-total"
                type="number"
                min={0}
                max={100}
                invalid={!!form.formState.errors.totalDays}
                {...form.register('totalDays')}
              />
              <p className="mt-1 text-xs text-text-faint">
                Standardowo 26; 20 dla pracowników z &lt; 10 lat stażu.
              </p>
              {form.formState.errors.totalDays && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.totalDays.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="balance-used">Dni wykorzystane</Label>
              <Input
                id="balance-used"
                type="number"
                min={0}
                invalid={!!form.formState.errors.usedDays}
                {...form.register('usedDays')}
              />
              <p className="mt-1 text-xs text-text-faint">
                Automatycznie z zatwierdzonych wniosków.
              </p>
              {form.formState.errors.usedDays && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.usedDays.message}
                </p>
              )}
            </div>
          </div>

          {entry && (
            <p className="text-sm text-text-muted">
              Aktualnie:{' '}
              <span className="font-medium text-text-primary">
                {entry.balance.usedDays}
              </span>{' '}
              wykorzystane +{' '}
              <span className="font-medium text-text-primary">
                {entry.balance.pendingDays}
              </span>{' '}
              oczekujące ={' '}
              <span className="font-medium text-text-primary">
                {entry.balance.remainingDays}
              </span>{' '}
              pozostałe
            </p>
          )}

          <Button
            type="submit"
            loading={update.isPending}
            disabled={!form.formState.isDirty}
          >
            Zapisz bilans
          </Button>
        </form>
      )}
    </section>
  )
}
